import type { JSX } from 'react';
import { useState } from 'react';
import { ActionIcon, Badge, Button, Menu, Popover, Stack, Text, Textarea, Tooltip } from '@mantine/core';
import type { GroupView } from '../../api';
import { DateCell } from './DateCell';
import { EditableText } from '../../ui/EditableText';
import { ColorPicker } from './ColorPicker';
import { NumberCell } from './NumberCell';
import { colorFor, type OutlineRow } from './outline';
import type { ColumnKey, ColumnSpec, ColumnWidths } from './columns';
import type { BaselineDisplay } from './baselineView';
import { TASK_STATUS, type TaskStatusKey, formatDate, formatSigned, predecessorNotation } from '../../format';
import { HEADER_HEIGHT, ROW_HEIGHT } from './layout';

/** Everything the list can change. One object, so rows stay simple. */
export interface ListHandlers {
  onRenameTask: (taskId: string, name: string) => void;
  onRenameGroup: (groupId: string, name: string) => void;
  onRecodeTask: (taskId: string, code: string) => void;
  onRecodeGroup: (groupId: string, code: string) => void;
  onChangeDuration: (taskId: string, days: number) => void;
  onChangeStart: (taskId: string, date: string) => void;
  onChangeFinish: (taskId: string, date: string) => void;
  onClearConstraint: (taskId: string) => void;
  /** Spends float: delays the task until only this much is left. */
  onChangeFloat: (taskId: string, desired: number) => void;
  onColorTask: (taskId: string, color: string | null) => void;
  onColorGroup: (groupId: string, color: string | null) => void;
  onDeleteTask: (taskId: string) => void;
  onDeleteGroup: (groupId: string) => void;
  /** Number of comments on a task (AP-108), shown next to its name. */
  commentCount?: (taskId: string) => number;
  onOpenComments?: (taskId: string) => void;
  /** Right-click on a row. */
  onRowContextMenu?: (event: React.MouseEvent, row: OutlineRow) => void;
  /** Management fields that do not affect the calculation. */
  onPatchTask: (
    taskId: string,
    body: Partial<{ status: TaskStatusKey; owner: string | null; position: string | null; deliverable: string | null; note: string | null; deadline: string | null }>,
  ) => void;
}

export interface PredecessorLink {
  code: string;
  type: string;
  lagDays: number;
}

const cell = (width: number, align?: 'right'): React.CSSProperties => ({
  width,
  flex: '0 0 auto',
  minWidth: 0,
  paddingRight: 8,
  textAlign: align,
});

export function TaskListHeader({
  columns,
  widths,
  onResize,
}: {
  columns: readonly ColumnSpec[];
  widths: ColumnWidths;
  onResize: (key: ColumnKey, width: number) => void;
}): JSX.Element {
  return (
    <div
      role="row"
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        height: HEADER_HEIGHT,
        padding: '0 0 6px 0',
        borderBottom: '1px solid var(--mantine-color-default-border)',
        position: 'sticky',
        top: 0,
        zIndex: 3,
        background: 'var(--mantine-color-body)',
      }}
    >
      {columns.map((column) => (
        <div
          key={column.key}
          role="columnheader"
          title={column.title}
          style={{ ...cell(widths[column.key], column.align), position: 'relative' }}
        >
          <Text size="xs" c="dimmed" fw={500} style={{ whiteSpace: 'nowrap' }}>
            {column.label}
          </Text>
          {!column.fixed && (
            <ColumnResizer
              startWidth={widths[column.key]}
              onDrag={(width) => onResize(column.key, Math.max(column.min, width))}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The grab strip on a column's right edge.
 *
 * The width follows the pointer's total travel since the press, measured from
 * the width at the press. An earlier version added each small movement to the
 * width it had seen at the press, so a long drag moved the edge by only the
 * last few pixels - the column hardly grew at all.
 */
function ColumnResizer({
  startWidth,
  onDrag,
}: {
  startWidth: number;
  onDrag: (width: number) => void;
}): JSX.Element {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Spaltenbreite ändern"
      title="Spaltenbreite ziehen"
      onPointerDown={(event) => {
        event.preventDefault();
        const origin = event.clientX;
        const base = startWidth;
        const move = (moveEvent: PointerEvent): void => {
          onDrag(resizedWidth(base, origin, moveEvent.clientX));
        };
        const stop = (): void => {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', stop);
          document.body.style.cursor = '';
          document.body.style.userSelect = '';
        };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', stop);
      }}
      style={{
        position: 'absolute',
        top: -8,
        right: 0,
        width: 7,
        height: HEADER_HEIGHT,
        cursor: 'col-resize',
        touchAction: 'none',
      }}
    />
  );
}

export function TaskListRows({
  rows,
  groups,
  columns,
  widths,
  collapsed,
  onToggleCollapse,
  handlers,
  baseline,
  predecessors,
}: {
  rows: OutlineRow[];
  groups: GroupView[];
  columns: readonly ColumnSpec[];
  widths: ColumnWidths;
  collapsed: ReadonlySet<string>;
  onToggleCollapse: (groupId: string) => void;
  handlers: ListHandlers;
  baseline: BaselineDisplay | null;
  predecessors: ReadonlyMap<string, readonly PredecessorLink[]>;
}): JSX.Element {
  return (
    <div role="rowgroup">
      {rows.map((row) => (
        <div
          key={row.id}
          role="row"
          className="pst-row"
          onContextMenu={handlers.onRowContextMenu ? (event) => handlers.onRowContextMenu?.(event, row) : undefined}
          style={{
            height: ROW_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid var(--mantine-color-default-border)',
            background: row.kind === 'group' ? 'var(--mantine-color-default)' : undefined,
            // The accent marks a phase and takes its colour. No indentation:
            // indenting the row shifted every column with it.
            borderLeft:
              row.kind === 'group'
                ? `3px solid ${row.group.color ?? 'var(--mantine-color-dimmed)'}`
                : '3px solid transparent',
          }}
        >
          {columns.map((column) => (
            <div
              key={column.key}
              role="cell"
              style={cell(widths[column.key], column.align)}
            >
              {row.kind === 'group'
                ? groupCell(column.key, row, collapsed.has(row.id), onToggleCollapse, handlers)
                : taskCell(column.key, row, groups, handlers, baseline, predecessors)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// --- cells ---------------------------------------------------------------

/** Row controls: delete first, then colour. Far left, away from the dates. */
function Gutter({
  label,
  onDelete,
  color,
  inherited,
  onColor,
}: {
  label: string;
  onDelete: () => void;
  color: string | null;
  inherited?: string | null;
  onColor: (next: string | null) => void;
}): JSX.Element {
  return (
    <div className="pst-gutter" style={{ display: 'flex', alignItems: 'center', gap: 2, paddingLeft: 4 }}>
      <Tooltip label={label} openDelay={400}>
        <ActionIcon size="xs" variant="subtle" color="critical" aria-label={label} onClick={onDelete}>
          {'×'}
        </ActionIcon>
      </Tooltip>
      <ColorPicker value={color} inherited={inherited} onChange={onColor} />
    </div>
  );
}

function groupCell(
  key: ColumnKey,
  row: Extract<OutlineRow, { kind: 'group' }>,
  collapsed: boolean,
  onToggle: (groupId: string) => void,
  handlers: ListHandlers,
): JSX.Element | null {
  const { group } = row;
  switch (key) {
    case 'gutter':
      return (
        <Gutter
          label="Phase löschen"
          onDelete={() => handlers.onDeleteGroup(group.id)}
          color={group.color}
          onColor={(next) => handlers.onColorGroup(group.id, next)}
        />
      );
    case 'code':
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="xs"
            onClick={() => onToggle(group.id)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Phase aufklappen' : 'Phase zuklappen'}
            style={{ visibility: row.hasChildren ? 'visible' : 'hidden' }}
          >
            {collapsed ? '▸' : '▾'}
          </ActionIcon>
          <EditableText
            value={group.code}
            monospace
            dimmed
            title="Gliederungsnummer ändern"
            onCommit={(next) => handlers.onRecodeGroup(group.id, next)}
          />
        </div>
      );
    case 'name':
      return (
        <EditableText
          value={group.name}
          bold
          title="Phase umbenennen"
          onCommit={(next) => handlers.onRenameGroup(group.id, next)}
        />
      );
    case 'duration':
      return (
        <Text size="sm" c="dimmed" ta="right" title="Ergibt sich aus den Vorgängen der Phase">
          {group.earlyStart ? group.durationDays : '—'}
        </Text>
      );
    case 'start':
      return <Text size="sm" fw={500}>{formatDate(group.earlyStart)}</Text>;
    case 'finish':
      return <Text size="sm" fw={500}>{formatDate(group.earlyFinish)}</Text>;
    case 'float':
      return null;
    case 'variance':
      return null;
    case 'status':
    case 'owner':
    case 'position':
    case 'deadline':
      return null;
    case 'predecessors':
      return (
        <Text size="xs" c="dimmed" title="Vorgänge in dieser Phase, einschließlich Unterphasen">
          {group.taskCount === 1 ? '1 Vorgang' : group.taskCount > 1 ? `${group.taskCount} Vorgänge` : ''}
        </Text>
      );
  }
}

function taskCell(
  key: ColumnKey,
  row: Extract<OutlineRow, { kind: 'task' }>,
  groups: GroupView[],
  handlers: ListHandlers,
  baseline: BaselineDisplay | null,
  predecessors: ReadonlyMap<string, readonly PredecessorLink[]>,
): JSX.Element | null {
  const { task } = row;
  const pinned = task.constraintType !== null;

  switch (key) {
    case 'gutter':
      return (
        <Gutter
          label="Vorgang löschen"
          onDelete={() => handlers.onDeleteTask(task.id)}
          color={task.color}
          inherited={colorFor(row, groups)}
          onColor={(next) => handlers.onColorTask(task.id, next)}
        />
      );
    case 'code':
      return (
        // Same x as a phase number, whose chevron sits in front of it.
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ width: 20, flex: '0 0 auto' }} />
          <EditableText
            value={task.code}
            monospace
            dimmed
            title="Nummer ändern"
            onCommit={(next) => handlers.onRecodeTask(task.id, next)}
          />
        </div>
      );
    case 'name':
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          {task.durationDays === 0 && (
            // The same diamond as in the chart, so list and bars say "milestone"
            // the same way.
            <Text size="xs" c="dimmed" title="Meilenstein" aria-label="Meilenstein">
              {'◆'}
            </Text>
          )}
          <EditableText
            value={task.name}
            bold={task.critical === true}
            title="Vorgang umbenennen"
            onCommit={(next) => handlers.onRenameTask(task.id, next)}
          />
          <DetailsButton
            deliverable={task.deliverable}
            note={task.note}
            onSave={(body) => handlers.onPatchTask(task.id, body)}
          />
          <CommentMark count={handlers.commentCount?.(task.id) ?? 0} onOpen={() => handlers.onOpenComments?.(task.id)} />
        </div>
      );
    case 'duration':
      return (
        <NumberCell
          value={task.durationDays}
          title="Dauer in Arbeitstagen ändern. 0 macht den Vorgang zum Meilenstein."
          onCommit={(next) => handlers.onChangeDuration(task.id, next)}
        />
      );
    case 'start':
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <DateCell
              value={task.earlyStart}
              pinned={pinned}
              title={
                pinned
                  ? `Terminvorgabe: Anfang nicht vor ${formatDate(task.constraintDate)}`
                  : 'Anfang ändern. Setzt die Terminvorgabe „Anfang nicht vor".'
              }
              onCommit={(next) => handlers.onChangeStart(task.id, next)}
            />
          </div>
          {pinned && (
            // The reset sits beside the date it resets, not in a far column.
            <ActionIcon
              size="xs"
              variant="subtle"
              aria-label="Terminvorgabe aufheben"
              title="Terminvorgabe aufheben"
              onClick={() => handlers.onClearConstraint(task.id)}
            >
              {'↺'}
            </ActionIcon>
          )}
        </div>
      );
    case 'finish':
      return (
        <DateCell
          value={task.earlyFinish}
          title="Ende ändern. Passt die Dauer an."
          onCommit={(next) => handlers.onChangeFinish(task.id, next)}
        />
      );
    case 'float':
      return task.totalFloat === null ? (
        <Text size="sm" c="dimmed" ta="right">{'—'}</Text>
      ) : (
        <NumberCell
          value={task.totalFloat}
          min={0}
          negative={task.totalFloat < 0}
          title={
            'Gesamtpuffer ändern. Ein kleinerer Wert verschiebt den Vorgang nach ' +
            'hinten und verbraucht Puffer. Mehr Puffer, als der Netzplan hergibt, ' +
            'lässt sich nicht einstellen.'
          }
          onCommit={(next) => handlers.onChangeFloat(task.id, next)}
        />
      );
    case 'variance':
      return <VarianceCell taskId={task.id} baseline={baseline} />;
    case 'status':
      return <StatusCell status={task.status} onChange={(status) => handlers.onPatchTask(task.id, { status })} />;
    case 'owner':
      return (
        <EditableText
          value={task.owner ?? ''}
          allowEmpty
          placeholder=" "
          title="Verantwortlich ändern"
          onCommit={(next) => handlers.onPatchTask(task.id, { owner: next || null })}
        />
      );
    case 'position':
      return (
        <EditableText
          value={task.position ?? ''}
          allowEmpty
          placeholder=" "
          title="Position oder Kategorie ändern"
          onCommit={(next) => handlers.onPatchTask(task.id, { position: next || null })}
        />
      );
    case 'deadline': {
      const missed = task.deadline !== null && task.earlyFinish !== null && task.earlyFinish > task.deadline;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <div style={{ flex: 1, minWidth: 0, color: missed ? 'var(--mantine-color-critical-6)' : undefined, fontWeight: missed ? 600 : undefined }}>
            <DateCell
              value={task.deadline}
              title={missed ? `Gerechnetes Ende ${formatDate(task.earlyFinish)} liegt nach dem spätesten Ende` : 'Spätestes Ende setzen. Verschiebt den Plan nicht.'}
              onCommit={(next) => handlers.onPatchTask(task.id, { deadline: next })}
            />
          </div>
          {task.deadline && (
            <ActionIcon size="xs" variant="subtle" aria-label="Spätestens entfernen" title="Spätestens entfernen" onClick={() => handlers.onPatchTask(task.id, { deadline: null })}>
              {'×'}
            </ActionIcon>
          )}
        </div>
      );
    }
    case 'predecessors': {
      const links = predecessors.get(task.id) ?? [];
      const text = predecessorNotation(links);
      return (
        <Text
          size="sm"
          ff="monospace"
          c="dimmed"
          title={text || 'Keine Vorgänger'}
          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {text}
        </Text>
      );
    }
  }
}

/**
 * Finish variance against the comparison baseline. Late is what matters, so it
 * carries colour; early or on time is shown plainly. A task added since the
 * baseline says so, rather than claiming a zero it does not have.
 */
function VarianceCell({
  taskId,
  baseline,
}: {
  taskId: string;
  baseline: BaselineDisplay | null;
}): JSX.Element | null {
  if (!baseline) return null;
  const variance = baseline.variances.get(taskId);
  if (!variance) return null;

  if (!variance.inBaseline) {
    return (
      <Text size="xs" c="dimmed" ta="right" title={`Nicht im Basisplan „${baseline.name}"`}>
        neu
      </Text>
    );
  }
  const days = variance.finishVariance;
  if (days === null) return <Text size="sm" c="dimmed" ta="right">{'—'}</Text>;

  const planned = baseline.planned.get(taskId);
  return (
    <Text
      size="sm"
      ta="right"
      fw={days > 0 ? 600 : 400}
      c={days > 0 ? 'critical.6' : 'dimmed'}
      title={planned ? `Basisplan „${baseline.name}": Ende ${formatDate(planned.finish)}` : undefined}
    >
      {formatSigned(days)}
    </Text>
  );
}

/** Status as a small coloured label that opens a choice on click. */
function StatusCell({ status, onChange }: { status: TaskStatusKey; onChange: (next: TaskStatusKey) => void }): JSX.Element {
  const current = TASK_STATUS[status];
  return (
    <Menu position="bottom-start" withinPortal>
      <Menu.Target>
        <Badge
          component="button"
          variant={status === 'open' ? 'outline' : 'light'}
          color={current.color}
          size="sm"
          radius="sm"
          style={{ cursor: 'pointer', textTransform: 'none', fontWeight: 500 }}
          aria-label={`Status: ${current.label}. Ändern`}
        >
          {current.label}
        </Badge>
      </Menu.Target>
      <Menu.Dropdown>
        {(Object.keys(TASK_STATUS) as TaskStatusKey[]).map((key) => (
          <Menu.Item key={key} onClick={() => key !== status && onChange(key)} fw={key === status ? 600 : 400}>
            {TASK_STATUS[key].label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

/** How many comments a task has; opens them. Nothing when there are none. */
function CommentMark({ count, onOpen }: { count: number; onOpen: () => void }): JSX.Element | null {
  if (count === 0) return null;
  const label = count === 1 ? '1 Kommentar' : `${count} Kommentare`;
  return (
    <Badge size="xs" variant="light" color="gray" component="button" onClick={onOpen} title={label} aria-label={label} style={{ cursor: 'pointer', flexShrink: 0, textTransform: 'none' }}>
      {count}
    </Badge>
  );
}

/**
 * Deliverable and note of a task, behind a small symbol next to its name.
 * Filled when there is something to read, faint when there is not.
 */
function DetailsButton({
  deliverable,
  note,
  onSave,
}: {
  deliverable: string | null;
  note: string | null;
  onSave: (body: { deliverable: string | null; note: string | null }) => void;
}): JSX.Element {
  const [opened, setOpened] = useState(false);
  const [draftDeliverable, setDraftDeliverable] = useState(deliverable ?? '');
  const [draftNote, setDraftNote] = useState(note ?? '');
  const hasContent = Boolean(deliverable || note);
  const summary = [deliverable ? `Ergebnis: ${deliverable}` : null, note].filter(Boolean).join('\n');

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      width={360}
      withArrow
      shadow="md"
      trapFocus
      onOpen={() => {
        setDraftDeliverable(deliverable ?? '');
        setDraftNote(note ?? '');
      }}
    >
      <Popover.Target>
        <Tooltip
          label={<span style={{ whiteSpace: 'pre-line' }}>{summary || 'Ergebnis und Notiz ergänzen'}</span>}
          multiline
          w={320}
          withArrow
          disabled={opened}
        >
          <ActionIcon
            size="xs"
            variant="subtle"
            color="gray"
            onClick={() => setOpened((o) => !o)}
            aria-label={hasContent ? `Ergebnis und Notiz: ${summary}` : 'Ergebnis und Notiz ergänzen'}
            style={{ flexShrink: 0, opacity: hasContent ? 1 : 0.35 }}
          >
            {'ⓘ'}
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSave({ deliverable: draftDeliverable.trim() || null, note: draftNote.trim() || null });
            setOpened(false);
          }}
        >
          <Stack gap="xs">
            <Textarea label="Ergebnis" description="Was vorliegt, wenn der Vorgang erledigt ist" autosize minRows={1} maxRows={4} value={draftDeliverable} onChange={(e) => setDraftDeliverable(e.currentTarget.value)} />
            <Textarea label="Notiz" autosize minRows={2} maxRows={8} value={draftNote} onChange={(e) => setDraftNote(e.currentTarget.value)} />
            <Button type="submit" size="xs">Übernehmen</Button>
          </Stack>
        </form>
      </Popover.Dropdown>
    </Popover>
  );
}

/** Width after dragging an edge from `origin` to `current`. Exported for tests. */
export function resizedWidth(base: number, origin: number, current: number): number {
  return base + (current - origin);
}
